/**
 * HTML Minify Tool
 *
 * Minify HTML by removing comments, collapsing whitespace.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

export const HtmlMinifyTool: ITransformerTool = {
  id: 'html-minify',
  name: 'HTML Minify',
  description: 'Minify HTML by removing comments and collapsing whitespace',
  version: '1.0.0',
  type: 'transformer',
  category: 'Formatters & Minifiers',
  keywords: ['html', 'minify', 'compress', 'compact'],

  seo: {
    slug: 'html-minifier',
    pageTitle: 'HTML Minifier - Free Online Tool | SWE DevTools',
    metaDescription: 'Minify HTML by removing comments and collapsing whitespace. Reduce HTML file size instantly. Fast, private, runs entirely in your browser.',
    keywords: ['html minifier', 'minify html online', 'html compress', 'html compact', 'remove html comments'],
    relatedTools: ['html-format', 'html-entity-encode', 'css-minify', 'js-minify'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.HTML, SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.HTML,

  attributes: [
    {
      name: 'removeComments',
      type: 'boolean',
      label: 'Remove Comments',
      defaultValue: true,
      description: 'Remove HTML comments (<!-- ... -->)',
    },
    {
      name: 'collapseWhitespace',
      type: 'boolean',
      label: 'Collapse Whitespace',
      defaultValue: true,
      description: 'Collapse multiple whitespace characters into a single space',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const removeComments = config.removeComments !== false;
    const collapseWhitespace = config.collapseWhitespace !== false;
    let html = String(input ?? '');
    if (!html.trim()) return '';

    try {
      // Remove HTML comments (but preserve conditional IE comments)
      if (removeComments) {
        html = html.replace(/<!--(?!\[if\s)[\s\S]*?-->/g, '');
      }

      if (collapseWhitespace) {
        // Preserve content in <pre>, <script>, <style>, <textarea> tags
        const preserveTags = /<(pre|script|style|textarea)\b[^>]*>[\s\S]*?<\/\1>/gi;
        const preserved: string[] = [];
        let idx = 0;

        html = html.replace(preserveTags, (match) => {
          const placeholder = `\x00PRESERVE_${idx}\x00`;
          preserved.push(match);
          idx++;
          return placeholder;
        });

        // Collapse whitespace between tags
        html = html.replace(/>\s+</g, '><');
        // Collapse runs of whitespace into a single space
        html = html.replace(/\s{2,}/g, ' ');

        // Restore preserved blocks
        for (let j = 0; j < preserved.length; j++) {
          html = html.replace(`\x00PRESERVE_${j}\x00`, preserved[j]);
        }
      }

      return html.trim();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to minify HTML';
      throw ToolError.execution(message);
    }
  },
};
